import { useEffect, useRef, useState } from 'react';

/* Because of legacy reasons, USER photo for teachers is the classroom photo,
    and the TEACHER_PROFILE photo is the user's profile photo. So when making requests to the server
    convert the string to match org.donorschoose.photos.PhotoProcessor.PhotoType */
export const ServerPhotoTypes = {
    USER: 'USER',
    TEACHER_PROFILE: 'TEACHER_PROFILE',
} as const;

type ServerPhotoType = (typeof ServerPhotoTypes)[keyof typeof ServerPhotoTypes] | null;

interface UsePhotoProcessingStatusOptions {
    teacherId: number;
    type: ServerPhotoType;
    enabled: boolean;
    pollIntervalMs?: number;
    maxAttempts?: number;
}

type PHOTO_PROCESSING_STATUS = 'IDLE' | 'POLLING' | 'PROCESSED' | 'ERROR' | 'TIMEOUT';
export const PhotoProcessingStatus = {
    IDLE: 'IDLE' as PHOTO_PROCESSING_STATUS,
    POLLING: 'POLLING' as PHOTO_PROCESSING_STATUS,
    PROCESSED: 'PROCESSED' as PHOTO_PROCESSING_STATUS,
    ERROR: 'ERROR' as PHOTO_PROCESSING_STATUS,
    TIMEOUT: 'TIMEOUT' as PHOTO_PROCESSING_STATUS,
};

interface PhotoProcessingResult {
    status: PHOTO_PROCESSING_STATUS;
    paths?: Record<string, string>;
    imageWidth?: number;
    imageHeight?: number;
    errorMessage?: string;
}

export function usePhotoProcessingStatus({
    teacherId,
    type,
    enabled,
    pollIntervalMs = 3000,
    maxAttempts = 20,
}: UsePhotoProcessingStatusOptions): PhotoProcessingResult {
    const [result, setResult] = useState<PhotoProcessingResult>({ status: PhotoProcessingStatus.IDLE });
    const attemptRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!enabled) {
            setResult({ status: PhotoProcessingStatus.IDLE });
            attemptRef.current = 0;
            return;
        }

        setResult({ status: PhotoProcessingStatus.POLLING });
        attemptRef.current = 0;

        const poll = () => {
            attemptRef.current++;

            if (attemptRef.current > maxAttempts) {
                setResult({ status: PhotoProcessingStatus.TIMEOUT, errorMessage: 'Photo processing timed out. Please try again.' });
                return;
            }

            $.ajax(`${secureBaseURL}/internal/photo/${teacherId}/status.json`, {
                method: 'GET',
                xhrFields: {
                    withCredentials: true,
                },
                data: {
                    type,
                },
            }).then((response) => {
                if (!response) {
                    setResult({ status: PhotoProcessingStatus.ERROR, errorMessage: response.message });
                    return;
                }

                const data = response.data;
                if (data.status === 'PROCESSED' || data.status === 'CROPPED') {
                    setResult({
                        status: PhotoProcessingStatus.PROCESSED,
                        paths: data.paths,
                        imageWidth: data.imageWidth,
                        imageHeight: data.imageHeight,
                    });
                } else if (data.status === 'PENDING') {
                    timerRef.current = setTimeout(poll, pollIntervalMs);
                } else {
                    setResult({ status: PhotoProcessingStatus.ERROR, errorMessage: `Unexpected status: ${data.status}` });
                }
            })
                .catch(() => {
                    setResult({ status: PhotoProcessingStatus.ERROR, errorMessage: 'Failed to check photo status. Please try again.' });
                });
        };

        poll();

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [enabled, teacherId, type, pollIntervalMs, maxAttempts]);

    return result;
}
